'use client'

import { paths } from '@/config/paths'
import { useInkeep } from '@/domain/hooks/useInkeep'
import { useArbitrageOpportunities } from '@/features/opportunities/logic/useArbitrageOpportunities'
import { Typography } from '@/ui/atoms/typography/Typography'
import { PageLayout } from '@/ui/layouts/PageLayout'
import { PageHero } from '@/ui/molecules/page-hero/PageHero'
import { ArrowLeftRight, ArrowRight, MessageCircleQuestion, Repeat } from 'lucide-react'
import { useRouter } from 'next/navigation'
import { Suspense } from 'react'
import { BorrowCardContent, BorrowCardSkeleton } from './components/BorrowCardContent'
import { CombinedEarnCardContent } from './components/CombinedEarnCardContent'
import { OfferingCard } from './components/OfferingCard'
import { OpportunitiesCardContent, OpportunitiesCardSkeleton } from './components/OpportunitiesCardContent'
import { UsdxlCardWrapper } from './components/UsdxlCardWrapper'
import { useLandingMarkets } from './logic/useLandingMarkets'

function OpportunitiesCardData() {
  const { opportunities: arbOpps, isLoading } = useArbitrageOpportunities()
  return <OpportunitiesCardContent topOpportunity={arbOpps[0]} totalCount={arbOpps.length} isLoading={isLoading} />
}

export function LandingPageContainer() {
  const router = useRouter()
  const { handleOpen: handleOpenChat } = useInkeep()
  const { data, isLoading } = useLandingMarkets()

  return (
    <PageLayout className="max-w-6xl">
      <div className="flex flex-col gap-16">
        {/* Row 1: Earn + Borrow + Placeholder (tallest, 1/3 each) */}
        <div className="flex flex-col gap-4">
          <PageHero title="HypurrFi" subtitle="Lend, borrow, and earn across optimized Hyperliquid markets" />
          <div className="grid grid-cols-1 gap-4 lg:grid-cols-3 md:grid-cols-2">
            <OfferingCard
              title="Earn"
              description="Earn yield on your assets across HypurrFi's optimized markets and curated DeFi vaults with institutional-grade risk management."
              size="featured"
              index={0}
              onClick={() => router.push(paths.earn)}
            >
              <CombinedEarnCardContent
                lendItems={data?.lend ?? []}
                earnItems={data?.earn ?? []}
                isLoading={isLoading}
              />
            </OfferingCard>

            <OfferingCard
              title="Borrow"
              description="Access the best borrow rates across HypurrFi markets. Use your crypto as collateral to borrow stablecoins and more."
              size="featured"
              index={1}
              onClick={() => router.push(paths.borrow)}
            >
              {isLoading ? <BorrowCardSkeleton /> : <BorrowCardContent items={data?.borrow ?? []} isLoading={false} />}
            </OfferingCard>

            <OfferingCard
              title="Migrate to Euler"
              description="Pooled markets are being sunset. Migrate your existing pooled positions to Euler to keep earning and borrowing."
              size="featured"
              index={2}
              onClick={() => router.push(paths.dashboardPooled)}
            >
              <div className="flex items-center gap-2 text-primary">
                <span className="font-medium text-sm">Go to pooled dashboard</span>
                <ArrowRight className="h-5 w-5" />
              </div>
            </OfferingCard>
          </div>

          <OfferingCard
            title="Have Questions?"
            description="Chat with our AI assistant to learn about HypurrFi products, strategies, and how to get started."
            size="cta"
            index={7}
            onClick={handleOpenChat}
          >
            <div className="flex items-center gap-2 text-muted-foreground">
              <MessageCircleQuestion className="h-5 w-5" />
              <span className="font-medium text-sm text-white">Ask AI</span>
            </div>
          </OfferingCard>
        </div>

        {/* Row 2: USDXL + Opportunities (50/50) */}
        <div className="flex flex-col gap-6">
          <div className="flex flex-col items-start gap-1">
            <Typography variant="h2" className="text-2xl sm:text-3xl" gradient>
              Strategies
            </Typography>
            <p className="max-w-lg text-muted-foreground">Go beyond lending — capture spreads and leverage USDXL.</p>
          </div>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <OfferingCard
              title="USDXL"
              description="The Hyperliquid-native dollar. Borrow, mint 1:1 with stablecoins, or earn yield with liquidity."
              size="large"
              index={3}
              onClick={() => router.push(paths.usdxl)}
            >
              <UsdxlCardWrapper />
            </OfferingCard>

            <OfferingCard
              title="Opportunities"
              description="Cross-market arbitrage — borrow low from one market, deposit high in another."
              size="large"
              index={4}
              onClick={() => router.push(paths.opportunities)}
            >
              <Suspense fallback={<OpportunitiesCardSkeleton />}>
                <OpportunitiesCardData />
              </Suspense>
            </OfferingCard>
          </div>
        </div>

        {/* Row 3: Swap + Card + Ask AI */}
        <div className="flex flex-col gap-6">
          <div className="flex flex-col items-start gap-1">
            <Typography variant="h2" className="text-2xl sm:text-3xl" gradient>
              More
            </Typography>
            <p className="max-w-lg text-muted-foreground">Tools and resources to get the most out of HypurrFi.</p>
          </div>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <OfferingCard
              title="Swap"
              description="Swap tokens directly on HyperEVM with competitive rates and low slippage."
              size="medium"
              index={5}
              onClick={() => router.push(paths.swap)}
            >
              <div className="flex items-center gap-2 text-muted-foreground">
                <Repeat className="h-5 w-5" />
                <span className="font-medium text-sm text-white">Swap any token</span>
              </div>
            </OfferingCard>

            <OfferingCard
              title="Bridge"
              description="Bridge assets to Hyperliquid from other chains quickly and securely."
              size="medium"
              index={6}
              onClick={() => router.push(paths.bridge)}
            >
              <div className="flex items-center gap-2 text-muted-foreground">
                <ArrowLeftRight className="h-5 w-5" />
                <span className="font-medium text-sm text-white">Bridge assets</span>
              </div>
            </OfferingCard>
          </div>
        </div>
      </div>
    </PageLayout>
  )
}
